import {memo, MouseEvent, useCallback, useMemo} from 'react'

import {CENTER_MODAL_TYPE, useCenterModal} from '@defign/ui'
import {useUserAgentContext} from '@financial/common-contexts'
import {Action, AUParam, sendAULog} from '@financial/common-logs'
import {CONNECT_SCHEME} from '@mydata/utils/connect'
import classNames from 'classnames/bind'

import {NotAgreeError} from '$commons/errors/memberErrors'
import {EXTERNAL_ROUTES} from '$constants/external-routes'
import {useLoginStatusContext} from '$contexts/member/userInfoStatus'
import {useNewConnectionUrl} from '$hooks/useConnectUrl'
import {useValidApp} from '$hooks/useValidApp'
import CONFIG from '$share/config'
import {MEMBER_STATUS} from '$types/member'
import buildUrl from '$utils/buildUrl'
import {sendClickNlog} from '$utils/nlog'

import Styles from './InsuranceConnectBanner.module.scss'

const cx = classNames.bind(Styles)

interface InsuranceConnectBannerProps {
    bannerType?: string
    nClickCode: string
    onButtonClick?: (e: MouseEvent<HTMLButtonElement>) => void
    auLogParam: AUParam
}

const InsuranceConnectBanner = memo(
    ({auLogParam, bannerType, nClickCode, onButtonClick}: InsuranceConnectBannerProps) => {
        const {isValidApp} = useValidApp()
        const {status, isSuccessLogin} = useLoginStatusContext()
        const {open, close} = useCenterModal()
        const {isMobile} = useUserAgentContext()

        const {enterConnectUrl} = useNewConnectionUrl({
            scheme: bannerType === 'detail-type' ? CONNECT_SCHEME.INSURANCE_FREE : CONNECT_SCHEME.INSURANCE_TIP,
        })

        const bannerInfo = useMemo(
            () =>
                bannerType === 'detail-type'
                    ? {
                          intro: '이 무료보험과 함께',
                          userText: null,
                          mainText: '내가 더 받을 수 있는 보험금은 얼마일까요?',
                      }
                    : {
                          intro: '보험사도 안 알려주는 TIP',
                          userText: null,
                          mainText: '놓치면 손해 보는 꿀팁 알려드릴게요',
                      },
            [bannerType],
        )

        const handleClickConfirm = useCallback(() => {
            if (status === MEMBER_STATUS.NOT_AGREED) {
                const error = new NotAgreeError(window.location.href)
                const url = isMobile ? error.redirectHref : error.pcRedirectHref
                window.location.href = url
            }

            if (status === MEMBER_STATUS.NONE) {
                const url = buildUrl({
                    baseUrl: CONFIG.URL.NID,
                    pathname: EXTERNAL_ROUTES.NID.LOGIN,
                    query: {
                        url: window.location.href,
                    },
                })
                window.location.href = url
            }
        }, [isMobile, status])

        function handleConnect(e: MouseEvent<HTMLButtonElement>) {
            sendClickNlog(nClickCode)

            if (isValidApp && !isSuccessLogin) {
                open({
                    type: CENTER_MODAL_TYPE.NOTICE,
                    content: {
                        title: (
                            <>
                                보험 서비스를 이용하려면 <br />
                                네이버페이 회원가입이 필요합니다.
                            </>
                        ),
                        description: <>회원가입 후, 더 편리하게 자산관리하세요!</>,
                    },
                    button: {
                        text: '네이버페이 간편가입하기',
                        variant: 'primary',
                        color: 'green',
                        onClick: () => {
                            handleClickConfirm()
                            close()
                        },
                    },
                    onClickDimmedLayer: close,
                })
                return
            }

            if (onButtonClick) {
                onButtonClick(e)
            } else {
                sendAULog(auLogParam, Action.Click)
                enterConnectUrl()
            }
        }

        return (
            <div className={cx('article', bannerType)}>
                <span className={cx('intro')}>{bannerInfo.intro}</span>
                {bannerInfo.userText}
                <span className={cx('message')}>{bannerInfo.mainText}</span>
                <button type="button" className={cx('link-connect')} onClick={handleConnect}>
                    알아보기
                </button>
            </div>
        )
    },
)

export default InsuranceConnectBanner
